/**
 * Contact preview for an import profile.
 *
 * A preview is a **sample** — up to ten normalized rows — and the handoff is
 * emphatic about what it is not:
 *
 *   • not a count, and not a guarantee of eligibility;
 *   • an empty first page is **not** proof the source is empty;
 *   • `permission: null` is unknown and must never be presented as opted in;
 *   • Brevo and ActiveCampaign previews are deliberately bounded to at most one
 *     contact, so a single row there says nothing about volume;
 *   • Google Sheets returns a message with `requires_permission: true` instead,
 *     because headers and mappings are validated in the import worker — so no
 *     contact preview may be promised for Sheets.
 */

import { Eye, Info, Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import { CONTACT_FIELDS } from "core/features/integrations";
import type { ContactField, Preview, Profile } from "core/features/integrations";
import type { IntegrationFailure } from "core/features/integrations";

const FIELD_LABEL: Record<ContactField, string> = {
    first_name: "First name",
    last_name: "Last name",
    email: "Email",
    phone_number: "Phone",
    whatsapp_number: "WhatsApp",
    messenger_id: "Messenger",
};

/** Apps whose preview is capped at one contact by design. */
const ONE_ROW_APPS = new Set(["brevo", "activecampaign"]);

export interface ProfilePreviewProps {
    profile: Profile;
    preview: Preview | null;
    loading: boolean;
    failure: IntegrationFailure | null;
    onPreview: () => void;
}

export function ProfilePreview({
    profile,
    preview,
    loading,
    failure,
    onPreview,
}: ProfilePreviewProps) {
    const isSheets = profile.app_id === "googlesheets";

    return (
        <section className="rounded-xl border border-line p-3">
            <div className="flex items-center justify-between gap-2">
                <h3 className="text-ui font-semibold text-content">Preview</h3>
                <Button size="sm" variant="secondary" loading={loading} onClick={onPreview}>
                    <Eye className="h-4 w-4" aria-hidden="true" />
                    {preview ? "Preview again" : "Preview"}
                </Button>
            </div>

            {isSheets && !preview && (
                <p className="mt-2 flex items-start gap-1.5 text-meta text-content-muted">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    Google Sheets does not return a contact preview — the headers and mapping are
                    checked when the import runs.
                </p>
            )}

            {loading && (
                <p className="mt-2 flex items-center gap-1.5 text-meta text-content-muted">
                    <Loader2
                        className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                        aria-hidden="true"
                    />
                    Fetching a sample…
                </p>
            )}

            {failure && !loading && (
                <p role="alert" className="mt-2 text-meta text-red-600 dark:text-red-400">{failure.message}</p>
            )}

            {preview && "requires_permission" in preview && (
                /* Sheets, and anything else the backend answers this way. */
                <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-surface-muted px-2.5 py-2 text-meta text-content-muted">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {preview.message}
                </p>
            )}

            {preview && "contacts" in preview && (
                <div className="mt-2">
                    {preview.contacts.length === 0 ? (
                        <p className="text-meta text-content-muted">
                            Nothing came back on the first page. That does not mean the source is
                            empty — run the import to find out.
                        </p>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-meta">
                                    <thead>
                                        <tr className="text-left text-content-muted">
                                            {CONTACT_FIELDS.filter((field) =>
                                                preview.contacts.some(
                                                    (contact) => contact[field] !== undefined,
                                                ),
                                            ).map((field) => (
                                                <th key={field} className="py-1 pr-3 font-medium">
                                                    {FIELD_LABEL[field]}
                                                </th>
                                            ))}
                                            <th className="py-1 font-medium">Permission</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.contacts.map((contact, index) => (
                                            <tr key={index} className="border-t border-line">
                                                {CONTACT_FIELDS.filter((field) =>
                                                    preview.contacts.some(
                                                        (row) => row[field] !== undefined,
                                                    ),
                                                ).map((field) => (
                                                    <td
                                                        key={field}
                                                        className="py-1.5 pr-3 text-content"
                                                    >
                                                        {contact[field] ?? "—"}
                                                    </td>
                                                ))}
                                                <td className="py-1.5">
                                                    <PermissionCell value={contact.permission} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <p className="mt-2 flex items-start gap-1.5 text-meta text-content-muted">
                                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                {ONE_ROW_APPS.has(profile.app_id)
                                    ? "This provider’s preview is limited to one contact by design. It is a sample, not a count."
                                    : "A sample of up to ten rows. It is not a count, and not a guarantee that these contacts are eligible."}
                            </p>
                        </>
                    )}
                </div>
            )}
        </section>
    );
}

/** `null` is unknown. It is never drawn as a yes. */
function PermissionCell({ value }: { value: boolean | null }) {
    if (value === true) {
        return <span className="text-emerald-700 dark:text-emerald-300">Opted in</span>;
    }
    if (value === false) {
        return <span className="text-red-600 dark:text-red-400">Not opted in</span>;
    }
    return <span className="text-amber-700 dark:text-amber-300">Unknown</span>;
}
